import { useCallback } from 'react';
import type { ChangeEvent } from 'react';
import type { Novel } from '../../data/samples';
import { parseFrontMatter } from '../../utils/markdownParser';

interface UseNovelFileImportOptions {
  customNovels: Novel[];
  updateCustomNovels: (novels: Novel[]) => void;
}

export const createNovelFromFile = (file: File, text: string): Novel => {
  const { data: meta } = parseFrontMatter(text);

  const title = meta.title || file.name.replace(/\.(md|txt)$/i, '');
  const author = meta.author || 'マイライブラリ';
  const description = meta.description || `${file.name} からインポートされたマークダウン小説。`;

  const gradients = [
    'from-stone-600 to-stone-900',
    'from-neutral-700 to-neutral-950',
    'from-zinc-650 to-zinc-900',
    'from-neutral-800 to-neutral-950',
  ];
  const randomGradient = gradients[Math.floor(Math.random() * gradients.length)];

  return {
    id: `custom-${meta.ncode || Date.now()}`,
    title,
    author,
    description,
    coverColor: randomGradient,
    content: text,
    scraped: meta.scraped || false,
    publisher: meta.publisher,
    ncode: meta.ncode,
    url: meta.url,
    downloadedAt: meta.downloaded_at,
    lastUpdatedAt: meta.last_updated_at,
    episodesCount: meta.episodes,
    type: meta.type || 'series',
    genre: meta.genre,
    tags: meta.tags,
    status: meta.status || 'ongoing',
    charCount: meta.char_count || text.length,
  };
};

interface UseNovelFileImportOptions {
  customNovels: Novel[];
  updateCustomNovels: (novels: Novel[]) => void;
  onConflict?: (existing: Novel, incoming: Novel) => void;
}

export const useNovelFileImport = ({
  customNovels,
  updateCustomNovels,
  onConflict,
}: UseNovelFileImportOptions) => {
  const handleFileUpload = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 100 * 1024 * 1024) {
        alert('ファイルサイズが大きすぎます。100MB以下のファイルを選択してください。');
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        const newNovel = createNovelFromFile(file, text);

        // 既存小説との衝突判定 (ncode または title が一致)
        const conflict = customNovels.find((novel) => {
          if (novel.ncode && newNovel.ncode && novel.ncode === newNovel.ncode) {
            return true;
          }
          return novel.title === newNovel.title;
        });

        if (conflict && onConflict) {
          onConflict(conflict, newNovel);
        } else {
          updateCustomNovels([newNovel, ...customNovels]);
        }
      };
      reader.onerror = () => {
        console.error('Failed to read file:', file.name, reader.error);
        alert('ファイルの読み込みに失敗しました。別のファイルをお試しください。');
      };
      reader.readAsText(file);
      e.target.value = '';
    },
    [customNovels, updateCustomNovels, onConflict],
  );

  return { handleFileUpload };
};
